import { AddRowAction } from 'components/Table/AddRowAction'
import { DeleteRowAction } from 'components/Table/DeleteRowAction'
import { DuplicateRowAction } from 'components/Table/DuplicateRowAction'
import EditableSelectCell from 'components/Table/EditableSelectCell'
import { EditableTextCell } from 'components/Table/EditableTextCell'
import { RecordingTypes } from 'lib/enums'
import { CameraRecordingComplexCell, CameraRecordingDataRow } from 'lib/types'
import { InputTypes, SelectionTypes } from '../../../lib/enums'
import { AddNoteAction } from 'components/Table/AddNoteRow'
import { Row } from '@tanstack/react-table'
import { ReactComponent as MagicWandIcon } from '../../../assets/icons/utility/magic-wand.svg'

export type OriginalRow = {
  original?: CameraRecordingDataRow
  index: number
}

export const renderPrimaryActionCell = (isVisible: (row: OriginalRow) => boolean, handleDeleteCameraGroup: (row) => void) => {
  return ({ row }) => {
    if (isVisible(row)) {
      return (
        <div className="row-action-container">
          <DeleteRowAction row={row} deleteClickHandler={handleDeleteCameraGroup} cameraGroupId={row.original.cameraGroupId} />
        </div>
      )
    }

    return null
  }
}

export const renderSecondaryActionCell = (
  isVisible: (row: OriginalRow) => boolean,
  handleDuplicateCameraGroupTableRow: (cameraGroupId: number) => void,
  addCameraStream: (cameraGroupId: number) => void,
  handleShowSubRow: (cameraGroupId: number) => void
) => {
  return ({ row }) => {
    if (isVisible(row)) {
      return (
        <div className="row-action-container">
          <DuplicateRowAction row={row} duplicateClickHandler={handleDuplicateCameraGroupTableRow} cameraGroupId={row.original.cameraGroupId} />
          <AddRowAction row={row} addClickHandler={addCameraStream} />
          <AddNoteAction row={row} addClickHandler={handleShowSubRow} />
        </div>
      )
    }

    return null
  }
}

export const renderDeleteActionCell = (
  isPrimaryVisible: (row: OriginalRow) => boolean,
  isChildVisible: (row: OriginalRow) => boolean,
  handleDeleteCameraGroup: (row) => void,
  handleDeleteStream: (row: OriginalRow) => void
) => {
  return ({ row }) => {
    if (isPrimaryVisible(row)) {
      return (
        <div className="row-action-container">
          <DeleteRowAction row={row} deleteClickHandler={handleDeleteCameraGroup} cameraGroupId={row.original.cameraGroupId} />
        </div>
      )
    }

    if (isChildVisible(row)) {
      const data = row.original
      return (
        <div className="row-action-container">
          <DeleteRowAction row={row} deleteClickHandler={() => handleDeleteStream({ original: data, index: row.index })} cameraGroupId={data.cameraGroupId} />
        </div>
      )
    }

    return null
  }
}

export const renderChildDeleteCell = (isVisible: (row: OriginalRow) => boolean, handleDeleteStream: (row: OriginalRow) => void) => {
  return ({ row }) => {
    if (!isVisible(row)) return null
    const data = row.original
    return (
      <div className="row-action-container">
        <DeleteRowAction row={row} deleteClickHandler={() => handleDeleteStream({ original: data, index: row.index })} cameraGroupId={data.cameraGroupId} />
      </div>
    )
  }
}

export const singleTextCell = (
  inputType: InputTypes,
  maximumNumber: number,
  minimumNumber: number,
  updateCellData: (row: Row<any>, columnId: string, value: string) => void,
  isVisible: (row: OriginalRow) => boolean,
  label?: string,
  customValue?: any,
  isDisabled?: boolean,
  inputCreator?: (value: string) => any,
  doubleCellInfoId?: string
) => {
  return ({ row, column, getValue }) =>
    isVisible(row) ? (
      <EditableTextCell
        inputId={'xsd__e2e-table-row-' + row.id + '-cell-input-' + column.id + (doubleCellInfoId ? '-' + doubleCellInfoId : '')}
        value={customValue !== undefined ? customValue : getValue}
        row={row}
        column={column}
        updateCellData={updateCellData}
        label={label}
        isDisabled={isDisabled}
        inputCreator={inputCreator}
        inputType={inputType}
        maximumNumber={maximumNumber}
        minimumNumber={minimumNumber}
      />
    ) : null
}

export const singleSelectCell = (
  selectionType: SelectionTypes,
  isVisible: (row: OriginalRow) => boolean,
  options: { label: string; value: string | number }[],
  updateCellData: (row: Row<any>, columnId: string, value: string) => void,
  customValue?: any,
  isDisabled?: boolean,
  inputCreator?: (value: string) => any,
  doubleCellInfoId?: string
) => {
  return ({ row, column, getValue }) =>
    isVisible(row) ? (
      <EditableSelectCell
        selectId={'xsd__e2e-table-row-' + row.id + '-cell-select-' + column.id + (doubleCellInfoId ? '-' + doubleCellInfoId : '')}
        value={customValue !== undefined ? customValue : getValue}
        row={row}
        column={column}
        updateMyData={updateCellData}
        options={options}
        isDisabled={isDisabled}
        inputCreator={inputCreator}
        selectionType={selectionType}
      />
    ) : null
}

const inputDataCreator = (changedValue: string, isConstant: boolean, originalData: any, isBaseline?: boolean): CameraRecordingComplexCell<number | string> => {
  return isConstant || isBaseline ? { continuous: changedValue, motion: originalData.motion } : { continuous: originalData.continuous, motion: changedValue }
}

export const doubleTextCell = (
  inputType: InputTypes,
  maximumNumber: number,
  minimumNumber: number,
  valueFieldName: string,
  updateCellData: (row: Row<any>, columnId: string, value: string) => void,
  labels?: string[],
  isDisabled?: ((row: OriginalRow) => boolean)[]
) => {
  return ({ row, column, getValue }) => {
    const isSpeedup = row.original.recording === RecordingTypes.Speedup
    const isConstant = row.original.recording === RecordingTypes.Constant
    const doubleCellValue = row.getValue(column.id)

    return (
      <>
        {singleTextCell(
          inputType,
          maximumNumber,
          minimumNumber,
          updateCellData,
          () => !isSpeedup,
          labels ? labels[0] : '',
          isConstant ? doubleCellValue.continuous : doubleCellValue.motion,
          isDisabled ? isDisabled[0]?.(row) : false,
          value => inputDataCreator(value, isConstant, row.original[valueFieldName])
        )({ row, column, getValue })}

        {singleTextCell(
          inputType,
          maximumNumber,
          minimumNumber,
          updateCellData,
          () => isSpeedup,
          labels ? labels[1] : '',
          doubleCellValue.continuous,
          isDisabled ? isDisabled[1]?.(row) : false,
          value => inputDataCreator(value, true, row.original[valueFieldName]),
          'baseline'
        )({ row, column, getValue })}

        {singleTextCell(
          inputType,
          maximumNumber,
          minimumNumber,
          updateCellData,
          () => isSpeedup,
          labels ? labels[2] : '',
          doubleCellValue.motion,
          isDisabled ? isDisabled[2]?.(row) : false,
          value => inputDataCreator(value, false, row.original[valueFieldName]),
          'speedup'
        )({ row, column, getValue })}
      </>
    )
  }
}

export const bitrateTextCell = (
  inputType: InputTypes,
  maximumNumber: number,
  minimumNumber: number,
  valueFieldName: string,
  updateCellData: (row: Row<any>, columnId: string, value: string) => void,
  labels?: string[],
  isDisabled?: ((row: OriginalRow) => boolean)[]
) => {
  const renderBitrateField = (
    row: Row<any>,
    column: any,
    getValue: any,
    config: {
      id: string
      type: 'continuous' | 'motion'
      tooltip: string
      labelIndex: number
      value: any
      isUserDefined: boolean
      isBaseline?: boolean
      doubleCellInfoId?: string
    }
  ) => (
    <div className="bitrate-cell-row">
      <input
        type="checkbox"
        id={config.id}
        className="bitrate-cell-checkbox"
        checked={config.isUserDefined}
        onChange={e => updateCellData(row, `${config.type}IsCustomBitrate`, e.target.checked.toString())}
        title={config.tooltip}
      />
      <div className="bitrate-cell-input">
        {singleTextCell(
          inputType,
          maximumNumber,
          minimumNumber,
          updateCellData,
          () => true,
          labels ? labels[config.labelIndex] : '',
          config.value,
          isDisabled ? isDisabled[config.labelIndex]?.(row) : false,
          value => inputDataCreator(value, config.isBaseline ?? false, row.original[valueFieldName]),
          config.doubleCellInfoId
        )({ row, column, getValue })}
      </div>
    </div>
  )

  return ({ row, column, getValue }) => {
    const isSpeedup = row.original.recording === RecordingTypes.Speedup
    const isConstant = row.original.recording === RecordingTypes.Constant
    const doubleCellValue = row.getValue(column.id)

    return (
      <div className="bitrate-cell-container">
        {!isSpeedup &&
          renderBitrateField(row, column, getValue, {
            id: `user-defined-${row.id}-single`,
            type: isConstant ? 'continuous' : 'motion',
            tooltip: 'Enable to manually set data rate',
            labelIndex: 0,
            value: isConstant ? doubleCellValue.continuous : doubleCellValue.motion,
            isUserDefined: isConstant ? row.original.continuousIsCustomBitrate ?? false : row.original.motionIsCustomBitrate ?? false,
            isBaseline: isConstant
          })}

        {isSpeedup && (
          <>
            {renderBitrateField(row, column, getValue, {
              id: `user-defined-${row.id}-continuous`,
              type: 'continuous',
              tooltip: 'Enable to manually set baseline data rate',
              labelIndex: 1,
              value: doubleCellValue.continuous,
              isUserDefined: row.original.continuousIsCustomBitrate ?? false,
              isBaseline: true,
              doubleCellInfoId: 'baseline'
            })}
            {renderBitrateField(row, column, getValue, {
              id: `user-defined-${row.id}-motion`,
              type: 'motion',
              tooltip: 'Enable to manually set speedup data rate',
              labelIndex: 2,
              value: doubleCellValue.motion,
              isUserDefined: row.original.motionIsCustomBitrate ?? false,
              isBaseline: false,
              doubleCellInfoId: 'speedup'
            })}
          </>
        )}
      </div>
    )
  }
}

export const doubleSelectCell = (
  selectionType: SelectionTypes,
  valueFieldName: string,
  options: { label: string; value: string | number }[],
  updateCellData: (row: Row<any>, columnId: string, value: string) => void
) => {
  return ({ row, column, getValue }) => {
    const isSpeedup = row.original.recording === RecordingTypes.Speedup
    const isConstant = row.original.recording === RecordingTypes.Constant
    const doubleCellValue = row.getValue(column.id)

    return (
      <>
        {singleSelectCell(
          selectionType,
          () => !isSpeedup,
          options,
          updateCellData,
          isConstant ? doubleCellValue.continuous : doubleCellValue.motion,
          false,
          value => inputDataCreator(value, isConstant, row.original[valueFieldName])
        )({ row, column, getValue })}
        {singleSelectCell(
          selectionType,
          () => isSpeedup,
          options,
          updateCellData,
          doubleCellValue.continuous,
          false,
          value => ({ continuous: value, motion: value }),
          'baseline'
        )({ row, column, getValue })}
        {singleSelectCell(
          selectionType,
          () => isSpeedup,
          options,
          updateCellData,
          doubleCellValue.motion,
          true,
          value => ({ continuous: value, motion: value }),
          'speedup'
        )({ row, column, getValue })}
      </>
    )
  }
}

export const doubleSelectCellWithAction = (
  selectionType: SelectionTypes,
  valueFieldName: string,
  options: { label: string; value: string | number }[],
  updateCellData: (row: Row<any>, columnId: string, value: string) => void,
  onActionClick: (row: Row<any>) => void
) => {
  return ({ row, column, getValue }) => {
    const isSpeedup = row.original.recording === RecordingTypes.Speedup
    const isConstant = row.original.recording === RecordingTypes.Constant
    const doubleCellValue = row.getValue(column.id)

    const primarySelect = (
      <>
        {singleSelectCell(
          selectionType,
          () => !isSpeedup,
          options,
          updateCellData,
          isConstant ? doubleCellValue.continuous : doubleCellValue.motion,
          false,
          value => inputDataCreator(value, isConstant, row.original[valueFieldName])
        )({ row, column, getValue })}
        {singleSelectCell(
          selectionType,
          () => isSpeedup,
          options,
          updateCellData,
          doubleCellValue.continuous,
          false,
          value => ({ continuous: value, motion: value }),
          'baseline'
        )({ row, column, getValue })}
      </>
    )

    return (
      <>
        <div className="xsd-select-with-action">
          <div className="xsd-select-with-action__field">{primarySelect}</div>
          <button
            id={`xsd__e2e-image-complexity-wizard-row-${row.id}`}
            type="button"
            className="row-action xsd-select-with-action__button"
            onClick={() => onActionClick(row)}
            title="Open Image Complexity Wizard"
            aria-label="Open Image Complexity Wizard">
            <MagicWandIcon className="msds-icon" aria-hidden="true" focusable="false" />
          </button>
        </div>
        {singleSelectCell(
          selectionType,
          () => isSpeedup,
          options,
          updateCellData,
          doubleCellValue.motion,
          true,
          value => ({ continuous: value, motion: value }),
          'speedup'
        )({ row, column, getValue })}
      </>
    )
  }
}

type OptionArray = { value: string; label: string }[]
type OptionMap = Record<string, string>

const optionValueToLabel = (value: string, options?: OptionArray | OptionMap) => {
  if (!options) return value

  // If options is an array of objects
  if (Array.isArray(options)) {
    const found = options.find(opt => opt.value === value.toString())
    return found ? found.label : value
  }

  // If options is an object/map
  if (typeof options === 'object') return options[value] ?? value

  return value
}

export const pdfCell = (isVisible: (row) => boolean, options?: { value: string; label: string }[]) => {
  return ({ row, column, getValue, cell }) => {
    const value = getValue ? getValue() : row[column.accessorKey]
    if (isVisible({ original: row, index: 0 })) {
      if (typeof value === 'object' && row.recording === RecordingTypes.Speedup) {
        return (
          <>
            <div>{optionValueToLabel(value.continuous, options)}</div>
            <div>{optionValueToLabel(value.motion, options)}</div>
          </>
        )
      } else if (typeof value === 'object' && row.recording === RecordingTypes.Constant) {
        return <div>{optionValueToLabel(value.continuous, options)}</div>
      } else if (typeof value === 'object') {
        return <div>{row.recording === RecordingTypes.Speedup || row.fps ? optionValueToLabel(value.motion, options) : optionValueToLabel(value.continuous, options)} </div>
      } else {
        return <div>{optionValueToLabel(value, options)} </div>
      }
    }
    return null
  }
}
